import { useEffect, useState } from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import List from '@mui/joy/List'
import ListItem from '@mui/joy/ListItem'
import ListItemButton from '@mui/joy/ListItemButton'
import ListItemContent from '@mui/joy/ListItemContent'
import Radio from '@mui/joy/Radio'
import Snackbar from '@mui/joy/Snackbar'
import Stack from '@mui/joy/Stack'
import Typography from '@mui/joy/Typography'
import { DEFAULT_LOAD_SORT_PRESET, LOAD_SORT_PRESET_KEYS, type LoadSortPresetKey } from '@numeo/types'
import { mobileLoadSortApi } from 'api/mobileLoadSort'

// The wording the app's Sort sheet shows for each preset (mobile `dispatcher.loads.sort*` strings).
const PRESET_COPY: Record<LoadSortPresetKey, { title: string; subtitle: string }> = {
    age: { title: 'Newest to oldest', subtitle: 'Sorted by age' },
    rpm: { title: 'Rate per mile', subtitle: 'Sorted by highest RPM' },
    price: { title: 'Price', subtitle: 'Sorted by highest price' },
    deadhead: { title: 'Deadhead', subtitle: 'Shortest distance (mi) to pickup' },
    pickup: { title: 'Pickup', subtitle: 'Sorted by earliest date' },
    'distance-high': { title: 'Distance: High', subtitle: 'Sorted by high to low' },
    'distance-low': { title: 'Distance: Low', subtitle: 'Sorted by low to high' },
}

/** The Sort sheet's order: the default first, the rest in their usual order. */
const sheetOrder = (defaultPreset: LoadSortPresetKey): LoadSortPresetKey[] => [defaultPreset, ...LOAD_SORT_PRESET_KEYS.filter((key) => key !== defaultPreset)]

interface SnackbarState {
    open: boolean
    message: string
    color: 'success' | 'danger'
}

export default function MobileLoadSortPage() {
    // `saved` is null until the live value has actually been read: showing the built-in default in its place would
    // present a guess as what the app is serving.
    const [saved, setSaved] = useState<LoadSortPresetKey | null>(null)
    const [selected, setSelected] = useState<LoadSortPresetKey>(DEFAULT_LOAD_SORT_PRESET)
    const [loading, setLoading] = useState(true)
    const [loadFailed, setLoadFailed] = useState(false)
    const [saving, setSaving] = useState(false)
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', color: 'success' })

    async function load() {
        setLoading(true)
        setLoadFailed(false)
        try {
            const preset = await mobileLoadSortApi.get()
            setSaved(preset)
            setSelected(preset)
        } catch (error) {
            console.error('Failed to load the mobile default sort', error)
            setLoadFailed(true)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        void load()
    }, [])

    async function save() {
        setSaving(true)
        try {
            const preset = await mobileLoadSortApi.setDefault(selected)
            setSaved(preset)
            setSelected(preset)
            setSnackbar({ open: true, message: 'Default sort saved', color: 'success' })
        } catch (error) {
            console.error('Failed to save the mobile default sort', error)
            setSnackbar({ open: true, message: 'Failed to save the default sort', color: 'danger' })
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={{ p: 3, maxWidth: 720 }}>
            <Typography level="h3" sx={{ mb: 1 }}>
                Mobile Load Sort
            </Typography>
            <Typography level="body-sm" sx={{ mb: 3 }}>
                Picks how the load boards in the mobile app are sorted when they open. The chosen option is listed first in the Sort sheet, and loads are sorted
                by it until the user picks another. Takes effect on the next app launch / foreground.
            </Typography>

            {loadFailed ? (
                <Alert
                    color="danger"
                    sx={{ mb: 3 }}
                    endDecorator={
                        <Button size="sm" variant="soft" color="danger" onClick={() => void load()}>
                            Retry
                        </Button>
                    }
                >
                    Could not read the current default, so it is not shown. Saving is disabled until it loads.
                </Alert>
            ) : null}

            <Card variant="outlined" sx={{ p: 0, mb: 3 }}>
                <List sx={{ '--ListItem-paddingY': '0px', '--ListItem-paddingX': '0px' }}>
                    {sheetOrder(selected).map((key, index) => (
                        <ListItem key={key} sx={{ borderTop: index === 0 ? 'none' : '1px solid', borderColor: 'divider' }}>
                            <ListItemButton onClick={() => setSelected(key)} disabled={saving || saved === null} sx={{ px: 2, py: 1.5 }}>
                                <Radio checked={key === selected} readOnly sx={{ mr: 2 }} />
                                <ListItemContent>
                                    <Typography level="title-sm">{PRESET_COPY[key].title}</Typography>
                                    <Typography level="body-xs">{PRESET_COPY[key].subtitle}</Typography>
                                </ListItemContent>
                                {index === 0 ? (
                                    <Chip color="primary" size="sm">
                                        Default
                                    </Chip>
                                ) : null}
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            </Card>

            <Stack direction="row" spacing={2} alignItems="center">
                <Button onClick={save} disabled={saving || saved === null || selected === saved} loading={saving}>
                    Save default
                </Button>
                {saved === null ? (
                    <Typography level="body-xs">Live: unknown</Typography>
                ) : selected === saved ? (
                    <Typography level="body-xs">Live: {PRESET_COPY[saved].title}</Typography>
                ) : (
                    <Typography level="body-xs">Unsaved — live is still {PRESET_COPY[saved].title}</Typography>
                )}
            </Stack>

            <Typography level="body-xs" sx={{ mt: 3, color: 'text.tertiary' }}>
                Path: <code>numeo_config.mobileLoadSort.defaultPreset</code>
            </Typography>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                color={snackbar.color}
                variant="soft"
            >
                {snackbar.message}
            </Snackbar>
        </Box>
    )
}
